import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDroplet } from '@fortawesome/free-solid-svg-icons';

/**
 * 💧 P2P summary panel — ใช้ซ้ำทุกหน้า report (admin/merchant/reseller)
 * แสดง ฝาก/ถอน/ค่าธรรมเนียม P2P แยกจากยอดปกติ (ไม่ปนตัวเลข)
 * ซ่อนอัตโนมัติถ้าไม่มี P2P ในช่วงเวลานั้น
 */
export interface P2pReportSummary {
  deposit_count: number;   // = peer-matched เท่านั้น (fallback แยกออกแล้ว)
  deposit_volume: number;
  peer_count: number;
  peer_volume: number;
  fallback_count?: number;  // เข้าบัญชีเว็บ (custodial, rate ปกติ) — นับรวมในยอดฝากปกติของ report หลักแล้ว
  fallback_volume?: number;
  fallback_fee?: number;
  payout_count: number;
  payout_volume: number;
  fee_payin: number;
  fee_payout: number;
  fee_total: number;
  merchant_count: number;
}

const baht = (v: number | string) =>
  '฿' + parseFloat(String(v ?? 0)).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v: number | string) => Number(v ?? 0).toLocaleString('th-TH');

const P2pReportPanel: React.FC<{ summary: P2pReportSummary | null; title?: string; showMerchantCount?: boolean }> = ({
  summary, title = 'P2P (แยกจากยอดปกติ)', showMerchantCount = true,
}) => {
  if (!summary) return null;
  const fallbackCount = Number(summary.fallback_count || 0);
  const has = Number(summary.deposit_count) > 0 || Number(summary.payout_count) > 0
    || Number(summary.fee_total) > 0 || fallbackCount > 0;
  if (!has) return null;

  return (
    <div className="mb-4 rounded-2xl border border-sky-100 dark:border-sky-900/40 bg-sky-50/40 dark:bg-sky-900/10 p-4">
      <div className="flex items-center gap-2 mb-3">
        <FontAwesomeIcon icon={faDroplet} className="text-sky-500" />
        <span className="text-sm font-bold text-sky-700 dark:text-sky-300">{title}</span>
        <span className="text-[11px] text-sky-500/70 dark:text-sky-400/60">ฝากผ่าน payout liquidity · เว็บบันทึกเงินต้น+เก็บค่าธรรมเนียม</span>
      </div>
      <div className={`grid grid-cols-2 ${showMerchantCount ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3`}>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-3 ring-1 ring-inset ring-sky-100 dark:ring-slate-700">
          <p className="text-[11px] text-gray-400 dark:text-slate-500">ฝาก P2P (จับคู่ {num(summary.peer_count)})</p>
          <p className="text-lg font-bold text-gray-800 dark:text-slate-100">{baht(summary.deposit_volume)}</p>
          <p className="text-[11px] text-gray-400 dark:text-slate-500">{num(summary.deposit_count)} รายการ</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-3 ring-1 ring-inset ring-sky-100 dark:ring-slate-700">
          <p className="text-[11px] text-gray-400 dark:text-slate-500">ถอน P2P (liquidity)</p>
          <p className="text-lg font-bold text-gray-800 dark:text-slate-100">{baht(summary.payout_volume)}</p>
          <p className="text-[11px] text-gray-400 dark:text-slate-500">{num(summary.payout_count)} รายการ</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-3 ring-1 ring-inset ring-sky-100 dark:ring-slate-700">
          <p className="text-[11px] text-gray-400 dark:text-slate-500">ค่าธรรมเนียม P2P (payin/payout)</p>
          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{baht(summary.fee_total)}</p>
          <p className="text-[11px] text-gray-400 dark:text-slate-500">{baht(summary.fee_payin)} / {baht(summary.fee_payout)}</p>
        </div>
        {showMerchantCount && (
          <div className="bg-white dark:bg-slate-800 rounded-xl p-3 ring-1 ring-inset ring-sky-100 dark:ring-slate-700">
            <p className="text-[11px] text-gray-400 dark:text-slate-500">ร้านที่มี P2P</p>
            <p className="text-lg font-bold text-gray-800 dark:text-slate-100">{num(summary.merchant_count)}</p>
            <p className="text-[11px] text-gray-400 dark:text-slate-500">ราย</p>
          </div>
        )}
      </div>
      {fallbackCount > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-slate-400 px-1">
          <span className="font-semibold text-gray-600 dark:text-slate-300">
            เข้าบัญชีเว็บ (จับคู่ไม่ได้): {num(fallbackCount)} รายการ · {baht(summary.fallback_volume || 0)} · fee {baht(summary.fallback_fee || 0)}
          </span>
          <span>— คิด rate ฝากปกติ และนับรวมในยอดฝาก/fee ของ report หลักแล้ว (ไม่ใช่ยอด P2P)</span>
        </div>
      )}
    </div>
  );
};

export default P2pReportPanel;
